import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import DrawRectangle from 'mapbox-gl-draw-rectangle-mode';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { Sidebar } from 'primereact/sidebar';
import { InputSwitch } from 'primereact/inputswitch';
import { Toast } from 'primereact/toast';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';

import { toast, registerToast } from '../MapboxMap/toastService';
import { circleTwoClickMode, markerCircleMode, DrawExtraControls, makeCircleFeature, drawStyles, restoreDragPan } from '../MapboxMap/drawModes';
import DrawnShapesList from '../MapboxMap/DrawnShapesList';
import ShapeAttributesEditor from '../MapboxMap/ShapeAttributesEditor';
import EmpreendimentoModal from '../MapboxMap/EmpreendimentoModal';
import MapCapture from '../MapCapture/MapCapture';

import geolocationService from '../../services/geolocationService';
import colors from '../../util/colors';

import FormFiltersGeo from './FormFiltersGeo';
import GeoResultsPanel from './GeoResultsPanel';
import GeoLegend from './GeoLegend';
import '../mapa.css';
import './GeoReferenciamentoMapbox.css';

const BRAZIL_CENTER = [-51.9253, -14.235];
const DEFAULT_ZOOM = 4;
const SELECTED_ZOOM = 12;
const SOURCE_ID = 'empreendimentos';
const LEADS_SOURCE_ID = 'leads';
const BAIRROS_SOURCE_ID = 'bairros';
const CLUSTER_LAYERS = ['clusters', 'cluster-count', 'empreendimentos-interesse', 'unclustered-point'];
const LEADS_LAYERS = ['leads-clusters', 'leads-cluster-count', 'leads-point'];
const EMPTY_FC = { type: 'FeatureCollection', features: [] };

// Ícones dos marcadores de empreendimento (mesmos nomes devolvidos por
// EmpreendimentosMapboxController::resolveIcon).
const ICON_LETTERS = ['E', 'O', 'S', 'M', 'A', 'L', 'X', 'H', 'C'];
const ICON_NAMES = [
    ...ICON_LETTERS.flatMap((l) => [`green_Marker${l}`, `red_Marker${l}`]),
    'yellow_MarkerF',
];

// Ícones dos leads — os mesmos arquivos usados pelo mapa Google (CustomMarkerUser).
const LEAD_ICONS = {
    lead_masc: '/img/markers/masc.png',
    lead_fem: '/img/markers/fem.png',
};

const INTEREST_COLOR = '#8e44ad';

// Cidades com limites de bairros (GeoJSON) disponíveis, no formato "UF-Cidade".
const BAIRROS_CITIES = new Set(window.bairrosCities || []);

const TAB_CONTENT_HEIGHT = '94vh';

const MAP_STYLE_STORAGE_KEY = 'geobrain.geo-mapbox.style';
const MAP_STYLES = {
    light: 'mapbox://styles/mapbox/streets-v12',
    dark: 'mapbox://styles/mapbox/dark-v11',
};
const readStoredMapStyle = () => {
    try {
        const v = localStorage.getItem(MAP_STYLE_STORAGE_KEY);
        return v === 'dark' || v === 'light' ? v : 'light';
    } catch {
        return 'light';
    }
};

const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
const GOOGLE_SCRIPT_ID = 'google-maps-places-script';

function loadGoogleMapsPlaces() {
    if (window.google?.maps?.places) return Promise.resolve();
    if (document.getElementById(GOOGLE_SCRIPT_ID)) {
        return new Promise((resolve) => {
            document.getElementById(GOOGLE_SCRIPT_ID).addEventListener('load', resolve, { once: true });
        });
    }
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = GOOGLE_SCRIPT_ID;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places&language=pt-BR&region=BR`;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });
}

const INITIAL_FILTERS = {
    cidades: [],
    incorporadoras: [],
    nome_empreendimento: [],
    status: ['Ativo'],
    quartos: [],
    user_type: [],
};

const normalizeName = (value) => String(value || '').trim().toLowerCase();

// Distância em metros entre dois pontos [lng, lat]. Substitui o cálculo do mapa
// Google, feito em SQL cru com `1000 * 4326` no lugar do raio da Terra.
const haversineMeters = ([lng1, lat1], [lng2, lat2]) => {
    const R = 6371000;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;

    return Math.round(2 * R * Math.asin(Math.sqrt(a)));
};

/**
 * Mapa de Georreferenciamento em Mapbox — sucessor do mapa Google
 * (GeoReferenciamentoMapa/DefaulMap.jsx).
 *
 * Origem dos dados:
 * - Empreendimentos: `api.empreendimentos-geo-mapbox` → EmpreendimentosMapboxController@index,
 *   100% MySQL. O mapa Google usava `POST /empreendimentos/json`, que lê a coleção
 *   MongoDB `empreendimentos` — base em desativação, proibida para features novas.
 * - Leads/clientes: `api.geo-referenciamento-leads-mapbox` (MySQL, tabela
 *   `geo_referencimentos`), já em GeoJSON.
 *
 * O recorte por formas desenhadas é feito no cliente (booleanPointInPolygon), como
 * no mapa Mapbox principal. No mapa Google isso era SQL geoespacial quebrado: o
 * filtro por polígono estava comentado e o por círculo comparava graus com metros.
 */
class GeoReferenciamentoMapbox extends Component {
    state = {
        left_side_bar_visible: true,
        right_side_bar_visible: false,
        active_tab: 'filtros',
        loading: false,
        filters: { ...INITIAL_FILTERS },
        mapStyle: readStoredMapStyle(),
        showEmpreendimentos: true,
        showLeads: true,
        showBairros: false,
        bairrosAvailable: false,
        totalEmpreendimentos: 0,
        totalLeads: 0,
        drawnShapes: [],
        areaResults: [],
        selectedBuilding: null,
        shapeEditorOpen: false,
        shapeEditing: null,
        empreendimentoDetail: null,
        showEmpreendimentoModal: false,
        loadingEmpreendimentoDetail: false,
        show_map_capture: false,
        mapCaptureData: null,
        mapCaptureCanvas: null,
    };

    containerRef = React.createRef();
    searchInputRef = React.createRef();
    toastRef = React.createRef();
    map = null;
    draw = null;
    drawExtras = null;
    currentGeojson = null;
    currentLeadsGeojson = null;
    // mapboxgl.Marker do empreendimento selecionado (o único marcador de HTML da página).
    selectedMarker = null;
    allFeatures = [];
    allLeadFeatures = [];
    // { [leadId]: metros } — recalculado a cada clique num empreendimento.
    leadDistances = {};
    // { "UF-Cidade": features[] } — evita rebaixar o GeoJSON a cada toggle.
    bairrosCache = {};
    // Visibilidade das sidebars antes de abrir a ficha do empreendimento.
    _sidebarsBeforeModal = null;
    _hidingIds = new Set();

    componentDidMount() {
        registerToast(this.toastRef);

        const apiKey = window.mapboxApiKey;
        if (!apiKey || !this.containerRef.current) {
            if (!apiKey) console.error('[GeoReferenciamentoMapbox] window.mapboxApiKey ausente');
            return;
        }

        mapboxgl.accessToken = apiKey;
        this.map = new mapboxgl.Map({
            container: this.containerRef.current,
            style: MAP_STYLES[this.state.mapStyle],
            center: BRAZIL_CENTER,
            zoom: DEFAULT_ZOOM,
            // Necessário para a captura de tela ler os pixels do canvas WebGL
            // (map.getCanvas().toDataURL()).
            preserveDrawingBuffer: true,
        });

        this.map.on('error', (e) => console.error('[GeoReferenciamentoMapbox] erro:', e.error));

        this.map.addControl(new mapboxgl.NavigationControl(), 'top-right');
        this.map.addControl(new mapboxgl.FullscreenControl(), 'top-right');
        this.map.addControl(
            new mapboxgl.GeolocateControl({
                positionOptions: { enableHighAccuracy: true },
                trackUserLocation: true,
            }),
            'top-right'
        );

        this.setupDrawTools();
        this.setupMapEvents();
        this.map.on('load', () => this.initializeLayers());
        this.setupGooglePlaces();
    }

    componentWillUnmount() {
        if (this.map) this.map.remove();
    }

    // ===== Camadas =====

    // Roda no 'load' e a cada 'style.load' — a troca de estilo destrói fontes,
    // camadas e imagens.
    initializeLayers = async () => {
        await this.loadMarkerImages();
        // Os bairros entram primeiro para ficarem por baixo dos marcadores.
        this.setupBairrosLayers();
        this.setupClusterLayers();
        this.setupLeadsLayers();

        if (this.currentGeojson) this.map.getSource(SOURCE_ID)?.setData(this.currentGeojson);
        if (this.currentLeadsGeojson) this.map.getSource(LEADS_SOURCE_ID)?.setData(this.currentLeadsGeojson);
        if (this.state.showBairros) this.renderBairros(this.state.filters.cidades);

        if (!this.state.showEmpreendimentos) this.setLayersVisibility(CLUSTER_LAYERS, false);
        if (!this.state.showLeads) this.setLayersVisibility(LEADS_LAYERS, false);

        // O filtro que esconde o ícone estático do selecionado vive na camada, então
        // precisa ser reaplicado depois de a troca de estilo recriá-la.
        this.applySelectedFilter(this.state.selectedBuilding?.id ?? null);
    };

    loadMarkerImages = () => {
        const images = {
            ...Object.fromEntries(ICON_NAMES.map((name) => [name, `/img/markers/${name}.png`])),
            ...LEAD_ICONS,
        };

        return Promise.all(
            Object.entries(images).map(
                ([name, url]) =>
                    new Promise((resolve) => {
                        if (this.map.hasImage(name)) return resolve();
                        this.map.loadImage(url, (err, image) => {
                            if (err || !image) return resolve();
                            if (!this.map.hasImage(name)) this.map.addImage(name, image);
                            resolve();
                        });
                    })
            )
        );
    };

    setupBairrosLayers = () => {
        if (this.map.getSource(BAIRROS_SOURCE_ID)) return;

        this.map.addSource(BAIRROS_SOURCE_ID, { type: 'geojson', data: EMPTY_FC });
        this.map.addLayer({
            id: 'bairros-fill',
            type: 'fill',
            source: BAIRROS_SOURCE_ID,
            paint: { 'fill-color': '#0d6efd', 'fill-opacity': 0.08 },
        });
        this.map.addLayer({
            id: 'bairros-outline',
            type: 'line',
            source: BAIRROS_SOURCE_ID,
            paint: { 'line-color': '#0d6efd', 'line-width': 1.5 },
        });
        this.map.addLayer({
            id: 'bairros-label',
            type: 'symbol',
            source: BAIRROS_SOURCE_ID,
            layout: {
                'text-field': ['get', 'name'],
                'text-size': 11,
                'text-allow-overlap': false,
            },
            paint: { 'text-color': '#0d6efd', 'text-halo-color': '#ffffff', 'text-halo-width': 1.2 },
        });
    };

    setupClusterLayers = () => {
        if (this.map.getSource(SOURCE_ID)) return;

        this.map.addSource(SOURCE_ID, {
            type: 'geojson',
            data: EMPTY_FC,
            cluster: true,
            clusterMaxZoom: 14,
            clusterRadius: 50,
        });

        this.map.addLayer({
            id: 'clusters',
            type: 'circle',
            source: SOURCE_ID,
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': ['step', ['get', 'point_count'], '#51bbd6', 50, '#f1f075', 200, '#f28cb1'],
                'circle-radius': ['step', ['get', 'point_count'], 18, 50, 24, 200, 30],
                'circle-stroke-width': 2,
                'circle-stroke-color': '#ffffff',
            },
        });

        this.map.addLayer({
            id: 'cluster-count',
            type: 'symbol',
            source: SOURCE_ID,
            filter: ['has', 'point_count'],
            layout: {
                'text-field': ['get', 'point_count_abbreviated'],
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 13,
            },
        });

        // Halo roxo nos empreendimentos que são "imóvel de interesse" de algum lead.
        // Substitui o override de pino roxo do mapa Google (MapWithADrawingManager).
        this.map.addLayer({
            id: 'empreendimentos-interesse',
            type: 'circle',
            source: SOURCE_ID,
            filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'interesse'], true]],
            paint: {
                'circle-radius': 12,
                'circle-color': INTEREST_COLOR,
                'circle-opacity': 0.35,
                'circle-stroke-width': 2,
                'circle-stroke-color': INTEREST_COLOR,
            },
        });

        this.map.addLayer({
            id: 'unclustered-point',
            type: 'symbol',
            source: SOURCE_ID,
            filter: ['!', ['has', 'point_count']],
            layout: {
                'icon-image': ['get', 'icon'],
                'icon-size': 0.6,
                'icon-allow-overlap': true,
                'icon-anchor': 'bottom',
            },
        });

    };

    setupLeadsLayers = () => {
        if (this.map.getSource(LEADS_SOURCE_ID)) return;

        this.map.addSource(LEADS_SOURCE_ID, {
            type: 'geojson',
            data: EMPTY_FC,
            cluster: true,
            clusterMaxZoom: 14,
            clusterRadius: 50,
        });

        // Cores diferentes das dos clusters de empreendimento para as duas camadas
        // não se confundirem no mesmo mapa.
        this.map.addLayer({
            id: 'leads-clusters',
            type: 'circle',
            source: LEADS_SOURCE_ID,
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': ['step', ['get', 'point_count'], '#7bd389', 50, '#3f9e5a', 200, '#1d6b38'],
                'circle-radius': ['step', ['get', 'point_count'], 16, 50, 22, 200, 28],
                'circle-stroke-width': 2,
                'circle-stroke-color': '#ffffff',
            },
        });

        this.map.addLayer({
            id: 'leads-cluster-count',
            type: 'symbol',
            source: LEADS_SOURCE_ID,
            filter: ['has', 'point_count'],
            layout: {
                'text-field': ['get', 'point_count_abbreviated'],
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12,
            },
            paint: { 'text-color': '#ffffff' },
        });

        this.map.addLayer({
            id: 'leads-point',
            type: 'symbol',
            source: LEADS_SOURCE_ID,
            filter: ['!', ['has', 'point_count']],
            layout: {
                'icon-image': ['get', 'icon'],
                'icon-allow-overlap': true,
                'icon-anchor': 'bottom',
            },
        });

    };

    // Handlers registrados **uma única vez**, no mount: a troca de estilo destrói as
    // camadas e faz setup*Layers rodar de novo, mas o objeto `map` sobrevive — se os
    // handlers ficassem no setup, cada troca de tema empilharia um listener a mais.
    // O mapbox-gl ignora eventos de camada inexistente, então registrar antes de
    // criá-las é seguro.
    setupMapEvents = () => {
        const buildingPopup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 12 });
        const leadPopup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 12 });

        const expandCluster = (sourceId, layerId) => (e) => {
            const features = this.map.queryRenderedFeatures(e.point, { layers: [layerId] });
            const clusterId = features[0]?.properties?.cluster_id;
            if (clusterId === undefined) return;

            this.map.getSource(sourceId).getClusterExpansionZoom(clusterId, (err, zoom) => {
                if (err) return;
                this.map.easeTo({ center: features[0].geometry.coordinates, zoom });
            });
        };

        const pointer = (visible) => () => {
            this.map.getCanvas().style.cursor = visible ? 'pointer' : '';
        };

        this.map.on('click', 'clusters', expandCluster(SOURCE_ID, 'clusters'));
        this.map.on('click', 'leads-clusters', expandCluster(LEADS_SOURCE_ID, 'leads-clusters'));
        this.map.on('click', 'unclustered-point', (e) => this.selectBuilding(e.features[0]));

        ['clusters', 'leads-clusters'].forEach((layer) => {
            this.map.on('mouseenter', layer, pointer(true));
            this.map.on('mouseleave', layer, pointer(false));
        });

        this.map.on('mouseenter', 'unclustered-point', (e) => {
            this.map.getCanvas().style.cursor = 'pointer';
            const f = e.features[0];
            const p = f.properties;

            const cityState = p.city && p.state ? `${p.city}/${p.state}` : (p.city || p.state || '');
            const addressLine = [p.address, p.neighborhood, cityState].filter((x) => x && x.length).join(', ')
                || 'Endereço não disponível';
            const interesse = p.interesse
                ? `<br/><small style="color:${INTEREST_COLOR}"><strong>Imóvel de interesse de usuários</strong></small>`
                : '';

            buildingPopup
                .setLngLat(f.geometry.coordinates.slice())
                .setHTML(
                    `<strong>${p.name || ''}</strong><br/><small>${addressLine}</small>${interesse}` +
                    '<br/><small class="text-muted">Clique para calcular a distância dos usuários</small>'
                )
                .addTo(this.map);
        });

        this.map.on('mouseleave', 'unclustered-point', () => {
            this.map.getCanvas().style.cursor = '';
            buildingPopup.remove();
        });

        this.map.on('mouseenter', 'leads-point', (e) => {
            this.map.getCanvas().style.cursor = 'pointer';
            const f = e.features[0];
            const p = f.properties;

            const endereco = [p.street, p.number, p.neighborhood, p.city].filter((x) => x && x.length).join(', ');
            const interesse = p.property_of_interest
                ? `<br/><small>Imóvel de interesse: ${p.property_of_interest}</small>`
                : '';
            const distancia = this.leadDistances[p.id] !== undefined
                ? `<br/><small><strong>Distância até ${this.state.selectedBuilding?.name}: ` +
                  `${this.leadDistances[p.id].toLocaleString('pt-BR')} m</strong></small>`
                : '';

            leadPopup
                .setLngLat(f.geometry.coordinates.slice())
                .setHTML(
                    `<strong>${p.user_type ? p.user_type.toUpperCase() : 'USUÁRIO'}</strong>` +
                    `<br/><small>${endereco || 'Endereço não disponível'}</small>${interesse}${distancia}`
                )
                .addTo(this.map);
        });

        this.map.on('mouseleave', 'leads-point', () => {
            this.map.getCanvas().style.cursor = '';
            leadPopup.remove();
        });
    };

    setLayersVisibility = (layerIds, visible) => {
        const v = visible ? 'visible' : 'none';
        layerIds.forEach((id) => this.map?.getLayer(id) && this.map.setLayoutProperty(id, 'visibility', v));
    };

    toggleEmpreendimentos = (enabled) => {
        this.setState({ showEmpreendimentos: enabled });
        this.setLayersVisibility(CLUSTER_LAYERS, enabled);
    };

    toggleLeads = (enabled) => {
        this.setState({ showLeads: enabled });
        this.setLayersVisibility(LEADS_LAYERS, enabled);
    };

    // ===== Limites de bairros =====

    handleToggleBairros = (enabled) => {
        this.setState({ showBairros: enabled });
        if (!this.map?.getSource(BAIRROS_SOURCE_ID)) return;

        if (!enabled) {
            this.map.getSource(BAIRROS_SOURCE_ID).setData(EMPTY_FC);
            return;
        }

        const { cidades } = this.state.filters;
        if (cidades.length === 0) toast.info('Selecione uma cidade para ver os limites de bairros.');
        this.renderBairros(cidades);
    };

    renderBairros = async (cidades) => {
        if (!this.map?.getSource(BAIRROS_SOURCE_ID)) return;

        const groups = await Promise.all((cidades || []).map((c) => this.fetchBairrosGeojson(c.value)));
        this.map.getSource(BAIRROS_SOURCE_ID)?.setData({
            type: 'FeatureCollection',
            features: groups.flat(),
        });
    };

    fetchBairrosGeojson = async (value) => {
        if (this.bairrosCache[value]) return this.bairrosCache[value];

        // A cidade pode ter hífen no nome, então só o primeiro separa a UF.
        const idx = value.indexOf('-');
        if (idx === -1) return [];
        const uf = value.slice(0, idx);
        const cidade = value.slice(idx + 1);

        try {
            const res = await axios.get(`/bairros/geojson/${encodeURIComponent(cidade)}/${encodeURIComponent(uf)}`);
            const features = res.data?.features || [];
            this.bairrosCache[value] = features;

            return features;
        } catch (e) {
            console.error('[GeoReferenciamentoMapbox] erro ao carregar limites de bairros:', e);
            return [];
        }
    };

    // O switch só aparece quando alguma cidade escolhida tem GeoJSON de bairros
    // (window.bairrosCities, vindo de City::geojson_neighborhood).
    refreshBairrosAvailability = (cidades) => {
        const available = (cidades || []).some((c) => BAIRROS_CITIES.has(c.value));
        this.setState({ bairrosAvailable: available });

        if (!available && this.state.showBairros) {
            this.setState({ showBairros: false });
            this.map?.getSource(BAIRROS_SOURCE_ID)?.setData(EMPTY_FC);
        }
    };

    // Captura de tela do mapa (mesma implementação do MapboxMap.printMap). O MapBox
    // desenha em canvas WebGL, então lemos os pixels com toDataURL() — só o conteúdo
    // do mapa entra; controles e sidebar são DOM sobreposto e ficam de fora.
    printMap = async () => {
        if (!this.map) return;

        // Garante que o último frame foi desenhado antes de ler os pixels.
        await new Promise((resolve) => {
            this.map.once('idle', resolve);
            this.map.triggerRepaint();
        });

        const canvas = this.map.getCanvas();
        this.setState({
            mapCaptureCanvas: canvas,
            mapCaptureData: canvas.toDataURL('image/jpeg', 0.95),
            show_map_capture: true,
        });
    };

    closeMapCapture = () => {
        this.restoreDragPan();
        this.setState({ show_map_capture: false });
    };

    setMapStyle = (style) => {
        if (style !== 'light' && style !== 'dark') return;
        this.setState({ mapStyle: style });
        try { localStorage.setItem(MAP_STYLE_STORAGE_KEY, style); } catch { /* localStorage indisponível */ }

        const drawn = this.draw?.getAll();
        this.map.setStyle(MAP_STYLES[style]);
        this.map.once('style.load', () => {
            this.initializeLayers();
            if (drawn?.features?.length) this.draw.set(drawn);
        });
    };

    // ===== Ferramentas de desenho =====

    setupDrawTools = () => {
        this.draw = new MapboxDraw({
            displayControlsDefault: false,
            controls: { polygon: true, trash: true },
            userProperties: true,
            styles: drawStyles,
            modes: {
                ...MapboxDraw.modes,
                draw_rectangle: DrawRectangle,
                draw_circle_two_click: circleTwoClickMode,
                draw_marker_circle: markerCircleMode,
            },
        });
        this.map.addControl(this.draw, 'top-left');
        this.drawExtras = new DrawExtraControls({ draw: this.draw });
        this.map.addControl(this.drawExtras, 'top-left');

        this.map.on('draw.create', this.handleDrawCreate);
        this.map.on('draw.update', this.handleDrawUpdate);
        this.map.on('draw.delete', this.handleDrawDelete);

        // Rede de segurança: os três eventos marcam o fim de uma interação com as
        // formas, então é seguro devolver o arrasto ao mapa (ver restoreDragPan).
        ['draw.create', 'draw.update', 'draw.delete', 'draw.modechange'].forEach(
            (evento) => this.map.on(evento, this.restoreDragPan)
        );
    };

    // Ver restoreDragPan em MapboxMap/drawModes.js: sem isso o mapa perde o
    // clique-e-arrasto de vez quando um diálogo engole o mouseup de um arraste.
    restoreDragPan = () => restoreDragPan(this.map);

    drawnPolygons = () => (this.draw?.getAll().features || []).filter(
        (f) => f.geometry?.type === 'Polygon' || f.geometry?.type === 'MultiPolygon'
    );

    insideShape = (point, shape) => {
        try {
            return booleanPointInPolygon(point, shape);
        } catch {
            return false;
        }
    };

    // Recorta empreendimentos e leads pelas formas desenhadas e monta o resumo por
    // área consumido pelo painel de resultados. Sem formas, exibe tudo.
    applyShapeFilter = () => {
        if (!this.map?.getSource(SOURCE_ID)) return;

        const shapes = this.drawnPolygons();

        const buildings = shapes.length
            ? this.allFeatures.filter((p) => shapes.some((s) => this.insideShape(p, s)))
            : this.allFeatures;
        const leads = shapes.length
            ? this.allLeadFeatures.filter((p) => shapes.some((s) => this.insideShape(p, s)))
            : this.allLeadFeatures;

        this.currentGeojson = { type: 'FeatureCollection', features: buildings };
        this.currentLeadsGeojson = { type: 'FeatureCollection', features: leads };
        this.map.getSource(SOURCE_ID).setData(this.currentGeojson);
        this.map.getSource(LEADS_SOURCE_ID)?.setData(this.currentLeadsGeojson);

        const areaResults = shapes.map((shape) => {
            const meta = this.state.drawnShapes.find((s) => s.drawId === shape.id) || {};

            return {
                drawId: shape.id,
                name: meta.name || 'Área',
                color: meta.color || '#cccccc',
                type: meta.type || 'Polígono',
                radiusMeters: meta.radiusMeters || null,
                buildings: this.allFeatures.filter((p) => this.insideShape(p, shape)),
                leads: this.allLeadFeatures.filter((p) => this.insideShape(p, shape)),
            };
        });

        this.setState((prev) => ({
            totalEmpreendimentos: buildings.length,
            totalLeads: leads.length,
            areaResults,
            right_side_bar_visible: areaResults.length > 0 ? true : prev.right_side_bar_visible,
        }));
    };

    handleDrawCreate = (e) => {
        const feature = e.features?.[0];
        if (!feature || (feature.geometry?.type !== 'Polygon' && feature.geometry?.type !== 'MultiPolygon')) return;

        const shapeType = feature.properties?.shapeType;
        const typeLabels = { circle: 'Círculo', marker_circle: 'Círculo', rectangle: 'Retângulo' };
        const type = typeLabels[shapeType] || 'Polígono';
        const idx = this.state.drawnShapes.length;
        const color = colors[idx % colors.length];

        const styleAttrs = { fillColor: color, fillOpacity: 0.3, strokeColor: '#000000', strokeWeight: 3 };
        Object.entries(styleAttrs).forEach(([k, v]) => this.draw.setFeatureProperty(feature.id, k, v));

        const shape = {
            drawId: feature.id,
            type,
            name: `${type} ${idx + 1}`,
            color,
            visible: true,
            feature: this.draw.get(feature.id) || feature,
            center: feature.properties?.center || null,
            radiusMeters: feature.properties?.radiusMeters || null,
            ...styleAttrs,
        };

        this.setState((prev) => ({ drawnShapes: [...prev.drawnShapes, shape] }), this.applyShapeFilter);
    };

    handleDrawUpdate = (e) => {
        const atualizadas = e.features || [];
        if (atualizadas.length === 0) return;

        // Um único setState para todas as features, com o refiltro no callback: o
        // painel lê o raio de `drawnShapes` e precisa do estado já aplicado.
        this.setState((prev) => ({
            drawnShapes: prev.drawnShapes.map((s) => {
                const feat = atualizadas.find((f) => f.id === s.drawId);
                if (!feat) return s;

                return {
                    ...s,
                    feature: feat,
                    center: feat.properties?.center || s.center,
                    radiusMeters: feat.properties?.radiusMeters || s.radiusMeters,
                };
            }),
        }), this.applyShapeFilter);
    };

    handleDrawDelete = (e) => {
        // Esconder uma forma remove-a do draw; esses ids não devem sumir da lista.
        const deletedIds = (e.features || [])
            .map((f) => f.id)
            .filter((id) => {
                if (this._hidingIds.has(id)) { this._hidingIds.delete(id); return false; }
                return true;
            });
        if (deletedIds.length === 0) return;

        this.setState(
            (prev) => ({ drawnShapes: prev.drawnShapes.filter((s) => !deletedIds.includes(s.drawId)) }),
            this.applyShapeFilter
        );
    };

    // ===== Lista de formas (aba Ferramentas) =====

    // `applyShapeFilter` monta o painel a partir de `drawnShapes` (nome, cor, tipo,
    // raio), então quem altera a forma precisa refiltrar **depois** do setState —
    // daí o callback. Chamar os dois em sequência usaria o estado antigo e o card
    // ficaria com a cor/rótulo anteriores.
    updateShape = (drawId, patch, done) => this.setState((prev) => ({
        drawnShapes: prev.drawnShapes.map((s) => s.drawId === drawId ? { ...s, ...patch } : s),
    }), done);

    centerOnShape = (shape) => {
        const feat = shape.visible ? this.draw?.get(shape.drawId) : shape.feature;
        if (!feat?.geometry?.coordinates) return;
        const bounds = new mapboxgl.LngLatBounds();
        const walk = (coords) => Array.isArray(coords[0]) ? coords.forEach(walk) : bounds.extend(coords);
        walk(feat.geometry.coordinates);
        this.map.fitBounds(bounds, { padding: 60, duration: 600 });
    };

    toggleShapeVisibility = (shape) => {
        if (shape.visible) {
            const feat = this.draw?.get(shape.drawId) || shape.feature;
            this._hidingIds.add(shape.drawId);
            this.draw?.delete(shape.drawId);
            this.updateShape(shape.drawId, { visible: false, feature: feat }, this.applyShapeFilter);
        } else {
            const [id] = this.draw?.add(shape.feature) || [];
            this.updateShape(shape.drawId, { visible: true, drawId: id ?? shape.drawId }, this.applyShapeFilter);
        }
    };

    setShapeRadius = (shape, radiusMeters) => {
        if (!shape.center || !(radiusMeters > 0)) return;
        const shapeType = shape.feature?.properties?.shapeType || 'circle';
        const updated = makeCircleFeature(shape.center, radiusMeters, shapeType);
        updated.properties = {
            ...updated.properties,
            fillColor: shape.fillColor, fillOpacity: shape.fillOpacity,
            strokeColor: shape.strokeColor, strokeWeight: shape.strokeWeight,
        };
        updated.id = shape.drawId;
        if (shape.visible) this.draw?.add(updated);
        this.updateShape(shape.drawId, { radiusMeters, feature: updated }, this.applyShapeFilter);
    };

    editShapeAttributes = (shape) => this.setState({ shapeEditing: shape, shapeEditorOpen: true });

    closeShapeEditor = () => {
        // O diálogo pode ter engolido o mouseup de um arraste em andamento.
        this.restoreDragPan();
        this.setState({ shapeEditorOpen: false, shapeEditing: null });
    };

    applyShapeAttributes = (shape, attrs) => {
        if (!shape) return;
        const base = (shape.visible && this.draw?.get(shape.drawId)) || shape.feature;
        const updated = {
            ...base, id: shape.drawId,
            properties: {
                ...base.properties,
                fillColor: attrs.fillColor, fillOpacity: attrs.fillOpacity,
                strokeColor: attrs.strokeColor, strokeWeight: attrs.strokeWeight,
            },
        };
        if (shape.type === 'Círculo' && attrs.radiusMeters > 0 && shape.center) {
            const st = updated.properties?.shapeType || 'circle';
            const geo = makeCircleFeature(shape.center, attrs.radiusMeters, st);
            updated.geometry = geo.geometry;
            updated.properties = { ...updated.properties, radiusMeters: attrs.radiusMeters };
        }
        if (shape.visible) {
            this.draw?.add(updated);
            // Antes do changeMode: o novo onSetup do simple_select congela o estado
            // atual do dragPan (ver restoreDragPan).
            this.restoreDragPan();
            this.draw?.changeMode('simple_select', { featureIds: [] });
        }
        const name = (attrs.name || '').trim() || shape.name;
        const cor = attrs.fillColor === 'transparent' ? attrs.strokeColor : attrs.fillColor;
        this.updateShape(shape.drawId, {
            name, color: cor, fillColor: attrs.fillColor, fillOpacity: attrs.fillOpacity,
            strokeColor: attrs.strokeColor, strokeWeight: attrs.strokeWeight,
            radiusMeters: shape.type === 'Círculo' && attrs.radiusMeters > 0 ? attrs.radiusMeters : shape.radiusMeters,
            feature: updated,
        }, this.applyShapeFilter);
        this.closeShapeEditor();
    };

    removeShape = (shape) => {
        if (shape.visible) this.draw?.delete(shape.drawId);
        this._hidingIds.delete(shape.drawId);
        this.setState(
            (prev) => ({ drawnShapes: prev.drawnShapes.filter((s) => s.drawId !== shape.drawId) }),
            this.applyShapeFilter
        );
    };

    // ===== Busca de endereço (Google Places) =====

    setupGooglePlaces = () => {
        loadGoogleMapsPlaces()
            .then(() => {
                if (!this.searchInputRef.current) return;

                const autocomplete = new window.google.maps.places.Autocomplete(this.searchInputRef.current, {
                    fields: ['geometry', 'formatted_address', 'name'],
                    componentRestrictions: { country: 'br' },
                });
                autocomplete.addListener('place_changed', async () => {
                    const place = autocomplete.getPlace();
                    if (!place.geometry?.location) return;
                    const lng = place.geometry.location.lng();
                    const lat = place.geometry.location.lat();
                    this.map.flyTo({ center: [lng, lat], zoom: SELECTED_ZOOM, essential: true });
                    await this.loadFromAddress(lat, lng, [place]);
                });
                this.searchInputRef.current.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter') e.preventDefault();
                });
            })
            .catch((err) => console.error('[GeoReferenciamentoMapbox] falha ao carregar Google Places:', err));
    };

    // Endereço buscado → resolve cidade/UF, sincroniza o formulário e carrega o mapa.
    loadFromAddress = async (lat, lng, place) => {
        let reverse;
        try {
            reverse = await geolocationService.reverse(lat, lng, place);
        } catch {
            toast.error('Não foi possível identificar a localidade.');
            return;
        }

        const uf = reverse?.uf;
        const cidade = reverse?.cidade;
        if (!uf || !cidade) {
            toast.warn('Localidade sem cidade/UF identificável.');
            return;
        }

        await this.handleCidadesChange([{ value: `${uf}-${cidade}`, label: cidade }]);
        this.reloadDados();
    };

    // ===== Filtros =====

    handleFilterChange = (field, value) => {
        const listFields = ['cidades', 'incorporadoras', 'nome_empreendimento', 'status', 'quartos', 'user_type'];
        const normalized = listFields.includes(field) ? (value || []) : value;

        this.setState((prev) => ({ filters: { ...prev.filters, [field]: normalized } }));
    };

    // Escolher a cidade só atualiza o filtro: o enquadramento do mapa acontece no
    // Buscar, via fitBoundsToLoadedFeatures, que ajusta aos resultados reais. O mapa
    // de fornecedores faz um flyTo aqui usando um geocoder externo (apicrawley) —
    // dispensável neste mapa, e uma chamada a menos para fora do Geobrain.
    handleCidadesChange = (cidades) => new Promise((resolve) => {
        this.setState((prev) => ({ filters: { ...prev.filters, cidades } }), () => {
            this.refreshBairrosAvailability(cidades);
            if (this.state.showBairros) this.renderBairros(cidades);
            resolve();
        });
    });

    cleanState = () => {
        (this.draw?.getAll().features || []).forEach((f) => this.draw.delete(f.id));
        this.allFeatures = [];
        this.allLeadFeatures = [];
        this.currentGeojson = null;
        this.currentLeadsGeojson = null;
        this.leadDistances = {};
        this.renderSelectedMarker(null);
        this.map?.getSource(SOURCE_ID)?.setData(EMPTY_FC);
        this.map?.getSource(LEADS_SOURCE_ID)?.setData(EMPTY_FC);
        this.map?.getSource(BAIRROS_SOURCE_ID)?.setData(EMPTY_FC);
        if (this.searchInputRef.current) this.searchInputRef.current.value = '';

        this.setState({
            filters: { ...INITIAL_FILTERS },
            drawnShapes: [],
            areaResults: [],
            selectedBuilding: null,
            totalEmpreendimentos: 0,
            totalLeads: 0,
            showBairros: false,
            bairrosAvailable: false,
            right_side_bar_visible: false,
        });
    };

    // ===== Carga dos dados =====

    // Empreendimentos: mesma validação de EmpreendimentosMapboxController.
    // Atenção à mudança de semântica em relação ao mapa Google: incorporadora e nome
    // do empreendimento viajam como **ids** (o Mongo recebia nomes) e o status
    // 'Comercialização' do Mongo corresponde a 'Ativo' no MySQL.
    buildMarkersPayload = () => {
        const { cidades, incorporadoras, nome_empreendimento, status, quartos } = this.state.filters;

        return {
            cidades: cidades.map((c) => c.value),
            status: status.length ? status : ['Ativo'],
            incorporadoras: incorporadoras.map((i) => i.value),
            nome_empreendimento: nome_empreendimento.map((n) => n.value),
            quartos,
        };
    };

    reloadDados = async () => {
        const { cidades, user_type } = this.state.filters;

        if (cidades.length === 0) {
            toast.warn('Selecione ao menos uma cidade.');
            return;
        }

        this.setState({ loading: true });

        try {
            const [empreendimentos, leads] = await Promise.all([
                axios.post(route('api.empreendimentos-geo-mapbox'), this.buildMarkersPayload()),
                axios.post(route('api.geo-referenciamento-leads-mapbox'), {
                    user_type,
                    cidades: cidades.map((c) => c.value),
                }),
            ]);

            this.allLeadFeatures = leads.data?.features || [];
            this.allFeatures = this.markInterestBuildings(empreendimentos.data?.features || []);

            // Nova busca invalida a seleção anterior (o empreendimento pode nem estar
            // no novo resultado).
            this.clearSelectedBuilding();
            this.applyShapeFilter();

            this.avisarResultadosVazios();

            if (this.allFeatures.length > 0 || this.allLeadFeatures.length > 0) {
                this.fitBoundsToLoadedFeatures();
            }
        } catch (e) {
            console.error('[GeoReferenciamentoMapbox] erro na busca:', e);
            toast.error('Erro ao carregar os dados do mapa.');
        } finally {
            this.setState({ loading: false });
        }
    };

    // As duas camadas são independentes: uma pode voltar vazia e a outra não, e o
    // aviso precisa dizer qual delas ficou sem resultado — senão o mapa parece
    // simplesmente incompleto.
    avisarResultadosVazios = () => {
        const semEmpreendimentos = this.allFeatures.length === 0;
        const semUsuarios = this.allLeadFeatures.length === 0;

        if (semEmpreendimentos && semUsuarios) {
            toast.warn('Nenhum empreendimento e nenhum usuário encontrado para os filtros informados.');
            return;
        }

        if (semEmpreendimentos) {
            toast.warn('Nenhum empreendimento encontrado para os filtros informados.');
        }

        if (semUsuarios) {
            toast.warn(
                'Nenhum usuário encontrado para os filtros informados. '
                + 'Os usuários vêm da sua importação em Georreferenciamento.'
            );
        }
    };

    // Marca os empreendimentos que aparecem como `property_of_interest` de algum lead.
    markInterestBuildings = (features) => {
        const interesses = new Set(
            this.allLeadFeatures
                .map((f) => normalizeName(f.properties?.property_of_interest))
                .filter((name) => name.length > 0)
        );

        return features.map((f) => ({
            ...f,
            properties: { ...f.properties, interesse: interesses.has(normalizeName(f.properties?.name)) },
        }));
    };

    fitBoundsToLoadedFeatures = () => {
        const features = [
            ...(this.currentGeojson?.features || []),
            ...(this.currentLeadsGeojson?.features || []),
        ];
        if (!features.length || !this.map) return;

        const bounds = new mapboxgl.LngLatBounds();
        features.forEach((f) => bounds.extend(f.geometry.coordinates));
        this.map.fitBounds(bounds, { padding: 60, maxZoom: 14, duration: 600 });
    };

    // ===== Distância dos leads até um empreendimento =====

    selectBuilding = (feature) => {
        const coordinates = feature.geometry.coordinates.slice();

        this.leadDistances = Object.fromEntries(
            this.allLeadFeatures.map((lead) => [
                lead.properties.id,
                haversineMeters(coordinates, lead.geometry.coordinates),
            ])
        );

        const selectedBuilding = {
            id: feature.properties.id,
            name: feature.properties.name,
            icon: feature.properties.icon,
            coordinates,
        };

        this.renderSelectedMarker(selectedBuilding);

        this.setState({
            selectedBuilding,
            // Abre o painel para o usuário ver qual empreendimento virou a referência
            // das distâncias — e conseguir limpar a seleção.
            right_side_bar_visible: true,
        });
    };

    clearSelectedBuilding = () => {
        this.leadDistances = {};
        this.renderSelectedMarker(null);
        this.setState({ selectedBuilding: null });
    };

    // Marcador do empreendimento selecionado, com animação de "pulo" (equivalente ao
    // google.maps.Animation.BOUNCE do mapa antigo). O mapbox-gl não anima símbolos de
    // camada, então o pino selecionado vira um mapboxgl.Marker de HTML — que sobrevive
    // à troca de estilo, por ser overlay do DOM e não parte do style.
    renderSelectedMarker = (building) => {
        this.selectedMarker?.remove();
        this.selectedMarker = null;

        if (!building || !this.map) {
            this.applySelectedFilter(null);
            return;
        }

        const element = document.createElement('div');
        element.className = 'geo-selected-marker';
        element.title = `${building.name || ''} — clique para limpar a seleção`;
        // O marcador fica sobre o canvas, então o clique não chega à camada: clicar
        // nele de novo desfaz a seleção.
        element.addEventListener('click', (e) => {
            e.stopPropagation();
            this.clearSelectedBuilding();
        });

        const img = document.createElement('img');
        img.src = `/img/markers/${building.icon}.png`;
        img.alt = '';
        element.appendChild(img);

        this.selectedMarker = new mapboxgl.Marker({ element, anchor: 'bottom' })
            .setLngLat(building.coordinates)
            .addTo(this.map);

        this.applySelectedFilter(building.id);
    };

    // Usuários exibidos no mapa (portanto já recortados pelas formas desenhadas)
    // ordenados pela distância até o empreendimento selecionado, do mais próximo ao
    // mais distante. As distâncias vêm de `leadDistances`, calculadas no clique.
    leadsPorDistancia = () => {
        if (!this.state.selectedBuilding) return [];

        return (this.currentLeadsGeojson?.features || [])
            .map((lead) => ({
                ...lead.properties,
                coordinates: lead.geometry.coordinates,
                distancia: this.leadDistances[lead.properties.id] ?? Infinity,
            }))
            .filter((lead) => Number.isFinite(lead.distancia))
            .sort((a, b) => a.distancia - b.distancia);
    };

    // Ficha completa do empreendimento — mesmo modal e mesmo payload do mapa Mapbox
    // principal (MapboxMap.openEmpreendimentoModal), servidos aqui pelas rotas
    // `api.empreendimentos-geo-mapbox.*`, sob a permissão deste módulo.
    openEmpreendimentoModal = async (buildingId) => {
        if (!buildingId || this.state.loadingEmpreendimentoDetail) return;

        this.setState({ loadingEmpreendimentoDetail: true });
        try {
            const res = await axios.get(route('api.empreendimentos-geo-mapbox.detalhe', buildingId));
            // O `pdf` vem do MapboxExportDataService apontando para a rota do mapa
            // principal, protegida por outra permissão; reaponta para a deste módulo.
            const empreendimentoDetail = {
                ...res.data,
                pdf: route('api.empreendimentos-geo-mapbox.ficha-pdf', buildingId),
            };
            // A ficha é larga; as sidebars saem da frente enquanto ela está aberta.
            // Guardamos o que estava visível para devolver ao fechar — o modal costuma
            // ser aberto a partir do painel direito, que faria falta depois.
            this._sidebarsBeforeModal = {
                left: this.state.left_side_bar_visible,
                right: this.state.right_side_bar_visible,
            };

            this.setState({
                empreendimentoDetail,
                showEmpreendimentoModal: true,
                left_side_bar_visible: false,
                right_side_bar_visible: false,
            });
        } catch (e) {
            console.error('[GeoReferenciamentoMapbox] erro ao carregar detalhe do empreendimento:', e);
            toast.error('Não foi possível carregar os detalhes do empreendimento.');
        } finally {
            this.setState({ loadingEmpreendimentoDetail: false });
        }
    };

    closeEmpreendimentoModal = () => {
        this.restoreDragPan();

        const anteriores = this._sidebarsBeforeModal;
        this._sidebarsBeforeModal = null;

        this.setState({
            showEmpreendimentoModal: false,
            empreendimentoDetail: null,
            ...(anteriores && {
                left_side_bar_visible: anteriores.left,
                right_side_bar_visible: anteriores.right,
            }),
        });
    };

    centerOnLead = (lead) => {
        if (!lead?.coordinates || !this.map) return;

        this.map.flyTo({ center: lead.coordinates, zoom: Math.max(this.map.getZoom(), 15), essential: true });
    };

    // Esconde o ícone estático do empreendimento selecionado para ele não ficar
    // sobreposto ao marcador que pula.
    applySelectedFilter = (selectedId) => {
        if (!this.map?.getLayer('unclustered-point')) return;

        this.map.setFilter(
            'unclustered-point',
            selectedId === null || selectedId === undefined
                ? ['!', ['has', 'point_count']]
                : ['all', ['!', ['has', 'point_count']], ['!=', ['get', 'id'], selectedId]]
        );
    };

    render() {
        const { left_side_bar_visible, right_side_bar_visible, active_tab, filters } = this.state;

        return (
            <>
                <div ref={this.containerRef} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} />

                <input
                    ref={this.searchInputRef}
                    type="text"
                    placeholder="Digite um endereço"
                    className="geo-mapbox-search"
                />

                {this.state.loading && (
                    <div className="mapbox-layer-loading" role="status">
                        <i className="fa fa-spin fa-spinner" /> Carregando dados do mapa…
                    </div>
                )}

                {/* Aleta lateral: a mesma abre e fecha a barra, acompanhando a borda dela
                    (substitui o "x" da Sidebar e o antigo botão de reabrir). */}
                {!this.state.showEmpreendimentoModal && (
                    <button
                        type="button"
                        className={`mapbox-flap mapbox-flap-left${left_side_bar_visible ? ' is-open' : ''}`}
                        title={left_side_bar_visible ? 'Ocultar barra lateral' : 'Mostrar barra lateral'}
                        aria-expanded={left_side_bar_visible}
                        onClick={() => this.setState({ left_side_bar_visible: !left_side_bar_visible })}
                    >
                        <i className={`fas ${left_side_bar_visible ? 'fa-angle-double-left' : 'fa-angle-double-right'}`} />
                    </button>
                )}

                <Sidebar
                    visible={left_side_bar_visible}
                    onHide={() => this.setState({ left_side_bar_visible: false })}
                    modal={false}
                    dismissable={false}
                    showCloseIcon={false}
                    className="mapbox-sidebar"
                >
                    <Tabs activeKey={active_tab} onSelect={(e) => this.setState({ active_tab: e })} id="geo-mapbox-tabs">
                        <Tab eventKey="filtros" title="Filtros">
                            <div className="card-body" style={{ height: TAB_CONTENT_HEIGHT, overflowY: 'auto' }}>
                                <FormFiltersGeo
                                    filters={filters}
                                    loading={this.state.loading}
                                    onChange={this.handleFilterChange}
                                    onCidadesChange={this.handleCidadesChange}
                                    onSubmit={this.reloadDados}
                                    onClear={this.cleanState}
                                />
                            </div>
                        </Tab>

                        <Tab eventKey="ferramentas" title="Ferramentas">
                            <div className="card-body" style={{ height: TAB_CONTENT_HEIGHT, overflowY: 'auto' }}>
                                <div className="form-group">
                                    <label className="mb-2 d-block">Exibir no mapa</label>
                                    <div className="mapbox-tools-switches">
                                        <div className="d-flex align-items-center" title="A preferência fica salva neste navegador.">
                                            <InputSwitch
                                                inputId="check_dark_mode"
                                                checked={this.state.mapStyle === 'dark'}
                                                onChange={(e) => this.setMapStyle(e.value ? 'dark' : 'light')}
                                            />
                                            <label htmlFor="check_dark_mode" className="pl-1 mb-0 ms-2">Modo noturno</label>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <InputSwitch
                                                inputId="check_empreendimentos"
                                                checked={this.state.showEmpreendimentos}
                                                onChange={(e) => this.toggleEmpreendimentos(e.value)}
                                            />
                                            <label htmlFor="check_empreendimentos" className="pl-1 mb-0 ms-2">Exibir empreendimentos</label>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <InputSwitch
                                                inputId="check_leads"
                                                checked={this.state.showLeads}
                                                onChange={(e) => this.toggleLeads(e.value)}
                                            />
                                            <label htmlFor="check_leads" className="pl-1 mb-0 ms-2">Exibir usuários</label>
                                        </div>
                                        {this.state.bairrosAvailable && (
                                            <div className="d-flex align-items-center">
                                                <InputSwitch
                                                    inputId="check_bairros"
                                                    checked={this.state.showBairros}
                                                    onChange={(e) => this.handleToggleBairros(e.value)}
                                                />
                                                <label htmlFor="check_bairros" className="pl-1 mb-0 ms-2">Exibir limites de bairros</label>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <hr />

                                <div className="form-group">
                                    <label className="mb-2 d-block">Ações</label>
                                    <div className="mapbox-tools-actions">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={this.printMap}
                                            title="Efetua Captura de Tela do Mapa"
                                        >
                                            <i className="fas fa-camera mr-1" /> Capturar tela
                                        </button>
                                    </div>
                                </div>

                                <hr />

                                <div className="form-group">
                                    <label className="mb-2 d-block">No mapa</label>
                                    <span className="badge badge-secondary mr-2">
                                        {this.state.totalLeads} usuários
                                    </span>
                                    <span className="badge badge-secondary">
                                        {this.state.totalEmpreendimentos} empreendimentos
                                    </span>
                                    <small className="text-muted d-block mt-1">
                                        Ao desenhar áreas, os totais e o painel de resultados consideram
                                        apenas o que está dentro delas.
                                    </small>
                                </div>

                                {this.state.drawnShapes.length > 0 && (
                                    <>
                                        <hr />
                                        <label className="mb-2 d-block">Formas desenhadas</label>
                                        <DrawnShapesList
                                            shapes={this.state.drawnShapes}
                                            onCenter={this.centerOnShape}
                                            onToggleVisibility={this.toggleShapeVisibility}
                                            onSetRadius={this.setShapeRadius}
                                            onEditAttributes={this.editShapeAttributes}
                                            onRemove={this.removeShape}
                                        />
                                    </>
                                )}
                            </div>
                        </Tab>

                        <Tab eventKey="legendas" title="Legendas">
                            <div className="card-body" style={{ height: TAB_CONTENT_HEIGHT, overflowY: 'auto' }}>
                                <GeoLegend interestColor={INTEREST_COLOR} />
                            </div>
                        </Tab>
                    </Tabs>
                </Sidebar>

                <GeoResultsPanel
                    visible={right_side_bar_visible}
                    // A ficha do empreendimento cobre o mapa: a aleta sai da frente junto
                    // com as barras (o painel é reaberto ao fechar a ficha).
                    escondido={this.state.showEmpreendimentoModal}
                    areas={this.state.areaResults}
                    selectedBuilding={this.state.selectedBuilding}
                    leadsPorDistancia={this.leadsPorDistancia()}
                    onShow={() => this.setState({ right_side_bar_visible: true })}
                    onHide={() => this.setState({ right_side_bar_visible: false })}
                    onClearSelectedBuilding={this.clearSelectedBuilding}
                    onCenterLead={this.centerOnLead}
                    onOpenBuilding={this.openEmpreendimentoModal}
                    loadingBuildingDetail={this.state.loadingEmpreendimentoDetail}
                    onCenterArea={(area) => this.centerOnShape(
                        this.state.drawnShapes.find((s) => s.drawId === area.drawId) || {}
                    )}
                />

                {this.state.empreendimentoDetail && (
                    <EmpreendimentoModal
                        show={this.state.showEmpreendimentoModal}
                        empreendimento={this.state.empreendimentoDetail}
                        closeModalEmpreendimento={this.closeEmpreendimentoModal}
                        togglePainelEmpreendimento={this.closeEmpreendimentoModal}
                    />
                )}

                <MapCapture
                    visible={this.state.show_map_capture}
                    handleClose={this.closeMapCapture}
                    imageData={this.state.mapCaptureData}
                    canvas={this.state.mapCaptureCanvas}
                />

                <ShapeAttributesEditor
                    show={this.state.shapeEditorOpen}
                    shape={this.state.shapeEditing}
                    onHide={this.closeShapeEditor}
                    // O editor emite só o patch de atributos; a forma em edição vem
                    // do estado (mesma ligação do MapboxMap).
                    onApply={(attrs) => this.applyShapeAttributes(this.state.shapeEditing, attrs)}
                />

                <Toast ref={this.toastRef} position="top-right" />
            </>
        );
    }
}

export default GeoReferenciamentoMapbox;

if (document.getElementById('mapa-geo-referenciamento-mapbox')) {
    ReactDOM.render(<GeoReferenciamentoMapbox />, document.getElementById('mapa-geo-referenciamento-mapbox'));
}
